'use client';

import { c as _c } from "react/compiler-runtime";
import 'mapbox-gl/dist/mapbox-gl.css';
import type { GeoJSONSource, LngLatLike, Map } from 'mapbox-gl';
import { useEffect, useMemo, useRef } from 'react';
import { FeatureCollection } from 'geojson';
import mapboxgl from 'mapbox-gl';
import { useRouter } from 'next/navigation';
import type { Address } from '@personly/libs-types';
import { useSchemes } from '@personly/libs-providers';
import type { Configs } from '@/statics/configs';
import { toAddressLine } from '@/libs/addresses';
interface Props {
  address: Address;
  configs: Configs;
  zoom: number;
}
interface MapOptions {
  container: HTMLElement;
  style: string;
  zoom: number;
  center?: LngLatLike;
}
const AddressMap = t0 => {
  const $ = _c(29);
  const {
    address,
    configs,
    zoom
  } = t0;
  const {
    cssVariables
  } = useSchemes();
  const router = useRouter();
  const map = useRef(null);
  const mapContainer = useRef(null);
  let t1;
  if ($[0] !== address) {
    t1 = toAddressLine(address);
    $[0] = address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      address: t1
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== address.city.location.latitude || $[5] !== address.city.location.longitude) {
    t3 = {
      type: "Point",
      coordinates: [address.city.location.longitude, address.city.location.latitude]
    };
    $[4] = address.city.location.latitude;
    $[5] = address.city.location.longitude;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = {
      type: "FeatureCollection",
      features: [{
        type: "Feature",
        properties: t2,
        geometry: t3
      }]
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const data = t4;
  let t5;
  if ($[10] !== address.city.location.latitude || $[11] !== address.city.location.longitude || $[12] !== configs || $[13] !== data || $[14] !== zoom) {
    t5 = () => {
      if (map.current) {
        return;
      }
      mapboxgl.accessToken = configs.MAPBOX_PUBLIC_KEY;
      const mapOptions = {
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/streets-v11?optimize=true",
        zoom,
        center: [address.city.location.longitude, address.city.location.latitude]
      };
      map.current = new mapboxgl.Map(mapOptions);
      map.current.addControl(new mapboxgl.NavigationControl());
      map.current.on("load", () => {
        map.current.addSource("address", {
          type: "geojson",
          data
        });
        map.current.addLayer({
          id: "address-view",
          type: "symbol",
          source: "address",
          layout: {
            "text-field": ["get", "address"],
            "text-font": ["Arial Unicode MS Bold"],
            "text-size": 16
          },
          paint: {
            "text-color": "rgb(0,0,0)"
          }
        });
      });
    };
    $[10] = address.city.location.latitude;
    $[11] = address.city.location.longitude;
    $[12] = configs;
    $[13] = data;
    $[14] = zoom;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== address.city.location || $[17] !== configs || $[18] !== cssVariables || $[19] !== data || $[20] !== router || $[21] !== zoom) {
    t6 = [address.city.location, data, configs, zoom, cssVariables, router];
    $[16] = address.city.location;
    $[17] = configs;
    $[18] = cssVariables;
    $[19] = data;
    $[20] = router;
    $[21] = zoom;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (!map.current) {
        return;
      }
      if (!("geolocation" in navigator)) {
        return;
      }
      navigator.geolocation.getCurrentPosition(position => {
        map.current.setCenter([position.coords.longitude, position.coords.latitude]);
      });
    };
    t8 = [];
    $[23] = t7;
    $[24] = t8;
  } else {
    t7 = $[23];
    t8 = $[24];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[25] !== data) {
    t9 = () => {
      if (!map.current) {
        return;
      }
      if (!map.current.loaded()) {
        return;
      }
      map.current.getSource("address")?.setData(data);
    };
    t10 = [data];
    $[25] = data;
    $[26] = t10;
    $[27] = t9;
  } else {
    t10 = $[26];
    t9 = $[27];
  }
  useEffect(t9, t10);
  let t11;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div ref={mapContainer} className="h-full" />;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
};
export default AddressMap;